/**
 * Helpers for cascading an "apply to entire campaign" initiative-type change to
 * every sibling ad set. Both the Paid Media Weights Adsets tab and the Paid
 * Media Optimization Ad Set tab need the campaign's full ad set roster so the
 * cascade doesn't silently miss ad sets; this keeps that derivation in one place.
 */

const ADSETS_CUBE = "company_media_metrics_adsets"

/**
 * Build a `campaign_id -> Set(adset_id)` roster from raw (Cube-prefixed) ad set
 * rows. The rows are expected to be UNFILTERED (the complete company-wide ad set
 * list), so the roster covers every sibling — not just the ones a filtered or
 * paginated table happens to render.
 */
export function buildAdsetIdsByCampaign(
  rows: Array<Record<string, unknown>>,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>()
  for (const row of rows) {
    const campaignId = row[`${ADSETS_CUBE}.campaign_id`] as string | undefined
    const adsetId = row[`${ADSETS_CUBE}.adset_id`] as string | undefined
    if (!campaignId || !adsetId) continue
    const existing = map.get(campaignId) ?? new Set<string>()
    existing.add(adsetId)
    map.set(campaignId, existing)
  }
  return map
}

/**
 * The campaign's sibling ad set IDs for a (de-prefixed) table row. Returns an
 * empty array when the row has no campaign or the campaign isn't in the roster.
 */
export function getRowChildAdsetIds(
  rowData: Record<string, unknown>,
  adsetIdsByCampaign: Map<string, Set<string>>,
): string[] {
  const campaignId = rowData.campaign_id as string | undefined
  return campaignId ? Array.from(adsetIdsByCampaign.get(campaignId) ?? []) : []
}
